import { makeStyles } from '@mui/styles';
import Card from 'apollo-react/components/Card';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import Pencil from 'apollo-react-icons/Pencil';
import Trash from 'apollo-react-icons/Trash';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import DashboardTab from './DashboardTab';
import DashboardTabSelector from './DashboardTabSelector';
import {
  deleteDashboardPart,
  editDashboardTab,
  editDashboardTabClose,
  updateDashboardPart,
} from '../../../actions/DashboardConfigActions';
import ActiveBar from '../../../components/ActiveBar';
import withTranslation from '../../../components/withTranslation';

const useStyles = makeStyles({
  card: {
    height: '100%',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    marginLeft: 16,
    marginTop: 16,
    marginRight: 16,
  },
  subtitle: {
    marginLeft: 16,
    color: '#595959',
    fontSize: 14,
    lineHeight: 1.71,
  },
});

const DashboardPart = ({ mode, part, translateString }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = useState(part.selectedTabIndex ?? 0);

  const { tabs } = part;
  const tab = tabs[tabIndex];

  function handleTabEdit({ id }) {
    dispatch(editDashboardTab(id));
  }

  function handleTabChange(index) {
    setTabIndex(index);

    dispatch(updateDashboardPart(part.id, { part: { id: part.id, selectedTabIndex: index } }));
  }

  function handleTabAccept(data) {
    const { id } = data.part;
    dispatch(updateDashboardPart(id, data));
    dispatch(editDashboardTabClose());
  }

  function handlePartDelete(part) {
    const { id } = part;
    dispatch(deleteDashboardPart(id));
  }

  function handleTabEditCancel() {
    dispatch(editDashboardTabClose());
  }

  const tabProps = {
    part,
    tab,
    mode,
    onTabChange: handleTabChange,
    onAccept: handleTabAccept,
    onCancel: () => handleTabEditCancel(tab),
    onEdit: () => handleTabEdit(tab),
    onDelete: () => handlePartDelete(part),
  };

  if (/table|bigtable/.test(tab.visualization)) {
    return <DashboardTab {...tabProps} />;
  }

  const { onEdit, onDelete, onTabChange } = tabProps;

  return (
    <Card className={classes.card}>
      <ActiveBar visible={mode === 'edit'} />

      <Grid container justify="space-between">
        <Grid item className={classes.titleContainer}>
          <Grid item>
            {tabs.length > 1 && (
              <DashboardTabSelector
                tabs={tabs}
                value={tabIndex}
                onChange={onTabChange}
                selectLabel={part.name ?? ''}
              />
            )}
            {tabs.length < 2 && (
              <Typography variant="title1" className={classes.title}>
                {translateString(tab.title)}
              </Typography>
            )}
            <Typography className={classes.subtitle}>{translateString(tab.subtitle)}</Typography>
          </Grid>
        </Grid>

        <Grid item style={{ padding: 10 }}>
          {mode !== 'edit' ? (
            <div>&nbsp;</div>
          ) : (
            <div>
              {tab.editable && (
                <Tooltip title={translateString(tabs.length === 1 ? 'Edit Part' : 'Edit Tab')}>
                  <IconButton
                    onClick={onEdit}
                    aria-label={translateString(tabs.length === 1 ? 'Edit Part' : 'Edit Tab')}
                  >
                    <Pencil />
                  </IconButton>
                </Tooltip>
              )}
              {tab.deletable && (
                <Tooltip title={translateString('Delete Part')}>
                  <IconButton onClick={onDelete} aria-label={translateString('Delete Part')}>
                    <Trash />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          )}
        </Grid>
      </Grid>

      <DashboardTab {...tabProps} />
    </Card>
  );
};

export default withTranslation(DashboardPart);
